<template>
  <RouterView v-slot="{ Component, route }">
    <component :is="getLayout(route)" v-bind="getLayoutProps(route)">
      <ErrorBoundary>
        <KeepAlive :include="keepAliveRoutes" :max="5">
          <component :is="Component" />
        </KeepAlive>
      </ErrorBoundary>
    </component>
  </RouterView>

  <DialogHost />

  <MobileSearchBar v-if="mobileSearchStore.isActive" />

  <div
    class="tablet-h:hidden flex w-full absolute items-center justify-center px-3"
    :class="toastClasses"
  >
    <Toast />
  </div>

  <Transition name="modal">
    <div
      v-if="uploadStore.showUploadBar"
      class="absolute bottom-2.5 left-0 w-full flex justify-center z-100"
    >
      <UploadBar />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { type Component, computed, onBeforeMount, onMounted, watch } from 'vue'
import {
  type RouteLocationNormalizedLoaded,
  parseQuery,
  RouterView,
  useRoute,
  useRouter,
} from 'vue-router'
import {
  useConfigQuery,
  useMeQuery,
  useBillingQueryQuery,
  type BillingResponse,
  type PlanResponse,
  useOnboardingQuery,
} from '@/shared/graphql/legacy/generated'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/shared/graphql/router'
import { getTokens, isSignedIn, setupAuth } from '@dacr/auth'
import { decodeJWT } from '@/shared/utils/jwt'

import DefaultLayout from '@/layouts/DefaultLayout.vue'
import ShellLayout from '@/layouts/ShellLayout.vue'
import ErrorBoundary from '@/shared/components/ErrorBoundary.vue'

import DialogHost from '@/shared/dialogs/DialogHost.vue'
import MobileSearchBar from '@/features/search/components/MobileSearchBar.vue'
import Toast from '@/shared/components/Toast.vue'
import UploadBar from '@/features/upload/components/UploadBar.vue'

import { useUserStore } from '@/shared/stores/user'
import { useConfigStore } from '@/shared/stores/config'
import { useBillingStore } from '@/features/billing/store'
import { useUploadStore } from '@/features/upload/store'
import {
  NativeActionsEnum,
  useWebviewStore,
  type PostMessageData,
} from '@/shared/stores/webview'
import { usePlatformStore } from '@/shared/stores/platform'
import { useOnboardingStore } from '@/features/onboarding/store'
import { useOriginStore } from '@/shared/stores/origin'
import { useMobileSearchStore } from '@/features/search/store'

import { useDevice } from '@/shared/composables/useDevice'
import { useEventListener } from '@/shared/composables/useEventListener'
import { Dialogs } from '@/shared/dialogs/types'
import { useDialog } from '@/shared/dialogs/useDialog'

const iOSWebviewSafeAreaResponseKey = '{"type":"webview:init"}'

interface iOSWebviewSafeAreaResponse {
  [iOSWebviewSafeAreaResponseKey]: {
    top?: number
    bottom?: number
  }
}

const layoutMap: Record<string, Component> = {
  default: DefaultLayout,
  shell: ShellLayout,
}

const getLayout = (route: RouteLocationNormalizedLoaded) => {
  const layout = route.meta.layout

  if (typeof layout !== 'string') {
    return 'div'
  }

  return layoutMap[layout] || DefaultLayout
}

const getLayoutProps = (route: RouteLocationNormalizedLoaded) => {
  if (typeof route.meta.layout !== 'string') {
    return {
      class: 'min-h-screen bg-white',
    }
  }

  return {
    sidebar: !!route.meta.sidebar,
  }
}

const keepAliveRoutes = [
  'FileExplorerFolderView',
  'CopyrightView',
  'FileExplorerView',
]

const userStore = useUserStore()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const uploadStore = useUploadStore()
const webviewStore = useWebviewStore()
const platformStore = usePlatformStore()
const onboardingStore = useOnboardingStore()
const originStore = useOriginStore()
const mobileSearchStore = useMobileSearchStore()
const device = useDevice()
const { open } = useDialog()
const route = useRoute()
const router = useRouter()

const enabled = computed(() => !!userStore.isSignedIn)

const { result: userData } = useMeQuery({
  enabled,
  pollInterval: 60_000,
})

const { result: configData } = useConfigQuery({
  enabled,
})

const { result: billingData } = useBillingQueryQuery(
  {},
  {
    enabled,
    pollInterval: 60_000,
  }
)

const { result: onboardingData } = useOnboardingQuery({
  enabled,
})

const { result: originsResult } = useQuery(
  graphql(`
    query Origins {
      origins {
        id
        name
        currency
        flag {
          url
        }
        logo {
          white {
            url
          }
          black {
            url
          }
        }
      }
    }
  `),
  {},
  {
    enabled,
    clientId: 'router',
  }
)

const toastClasses = computed(() => ({
  'bottom-[75px]': uploadStore.showUploadBar,
  'bottom-5': !uploadStore.showUploadBar,
}))

watch(userData, () => {
  if (!userData.value?.me) return

  localStorage.setItem('dacrUser', JSON.stringify(userData.value.me))
  userStore.setUser(userData.value.me)
})

watch(configData, () => {
  if (!configData.value?.config) return

  configStore.setConfig(configData.value.config)
})

watch(billingData, () => {
  if (!billingData.value?.billing) return

  billingStore.setBilling(billingData.value.billing as BillingResponse)
})

watch(
  onboardingData,
  () => {
    if (
      !onboardingData.value ||
      onboardingData.value.onboarding.completed ||
      route.path.includes('register') ||
      webviewStore.isActive
    )
      return

    if (onboardingData.value.onboarding.step === onboardingStore.maxSteps) {
      router.push('/onboarding/pro')
    } else {
      onboardingStore.step = (onboardingData.value.onboarding.step || 0) + 1
      router.push('/onboarding')
    }
  },
  {
    immediate: true,
    deep: true,
  }
)

watch(originsResult, () => {
  if (!originsResult.value?.origins) return

  originStore.setOrigins(originsResult.value.origins)
})

const handleShowUpgradeBundle = async () => {
  if (!billingStore.canUpdateSubscription) {
    return open(Dialogs.IAP, {
      props: { type: 'change' },
    })
  }

  await router.push('/settings/billing')

  setTimeout(() => {
    open(Dialogs.UpgradeBundleLimit)
  }, 500)
}

const handleWebviewMessage = async (event: MessageEvent) => {
  if (event.origin !== import.meta.env.VITE_URL) return

  const data = event.data as PostMessageData

  if (data.type === NativeActionsEnum.Plans) {
    const prev = JSON.stringify(billingStore.webviewPlans)
    const next = JSON.stringify(data.data)
    if (prev === next) return

    billingStore.setWebviewPlans(data.data as PlanResponse)
  }

  if (data.type === NativeActionsEnum.ShowUpgradeBundle) {
    await handleShowUpgradeBundle()
  }

  if (data.type === NativeActionsEnum.Locale) {
    platformStore.setLocale(data.data as string)
  }

  if (data.type === NativeActionsEnum.Resubscribe) {
    if (!billingStore.canUpdateSubscription) {
      return open(Dialogs.IAP, {
        props: { type: 'change' },
      })
    }

    open(Dialogs.ResubscribeIAP)
  }
}

onBeforeMount(async () => {
  if (!webviewStore.isActive) {
    await setupAuth({
      url: import.meta.env.VITE_AUTH_API,
      debug: import.meta.env.MODE === 'prod',
    })
  }

  window.gtag(
    'config',
    import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
    ...(isSignedIn()
      ? [
          {
            user_id: decodeJWT(getTokens().accessToken!)?.uid || '',
          },
        ]
      : [])
  )
})

onMounted(async () => {
  if (webviewStore.isActive) {
    useEventListener(window, 'message', handleWebviewMessage)

    const data = await webviewStore.postMessage({
      type: NativeActionsEnum.WebviewInit,
    })

    if (data && webviewStore.isiOS) {
      const webviewData = data as iOSWebviewSafeAreaResponse

      webviewStore.setTopSafeAreaheight(
        webviewData[iOSWebviewSafeAreaResponseKey].top || 0
      )
      webviewStore.setBottomSafeAreaHeight(
        webviewData[iOSWebviewSafeAreaResponseKey].bottom || 0
      )
    }
  }

  const searchParams = parseQuery(window.location.search)

  if (searchParams.showUpgradeBundle) {
    handleShowUpgradeBundle()
  }
})
</script>

<style>
* {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-variation-settings: 'slnt' 0;
}

#app {
  background-color: black;
}

#card > .__PrivateStripeElement {
  width: 100%;
}
</style>
